import type { PreOrderMetadata } from "@/app/api/types/preOrder";

/**
 * Merge junction-derived message ids into a pre-order's metadata.
 *
 * Linked WhatsApp/SMS messages get a highlight border in the recap chat panel,
 * driven by `pre_order_metadata.message_ids`. Old pre-orders carry that array in
 * their stored metadata; newer ones only have junction rows
 * (`pre_order_messages` / `pre_order_sms_messages`). This helper derives the
 * array from the junction ids so both code paths render identically.
 *
 * The junction `messageId` IS the message row id, so it matches the rendered
 * bubble's `data-message-id` directly — no id remapping needed.
 *
 * Union order: junction ids first (already chronologically ordered by the
 * caller), then any pre-existing `metadata.message_ids` not already present.
 * Result is deduped.
 *
 * Structural sharing: returns the ORIGINAL `metadata` reference unchanged when
 * the merge adds no new id — so React memo consumers downstream don't see a
 * spurious new object. The comparison is set-based, not positional: a stored
 * `message_ids` whose order differs from the junction-sorted result (e.g.
 * stored `["m2","m1"]` vs junction `["m1","m2"]`) is still treated as
 * unchanged, since `message_ids` is consumed as a set. Returns `null` only
 * when both the base metadata and the junction ids are empty.
 */
export function mergeMessageIdsIntoMetadata(
  metadata: PreOrderMetadata | null | undefined,
  junctionIds: readonly string[],
): PreOrderMetadata | null {
  const merged: string[] = [];
  const seen = new Set<string>();
  for (const id of junctionIds) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    merged.push(id);
  }
  const existing = Array.isArray(metadata?.message_ids)
    ? metadata.message_ids
    : [];
  for (const id of existing) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    merged.push(id);
  }

  if (!metadata) {
    return merged.length > 0 ? { message_ids: merged } : null;
  }

  // Preserve structural sharing when the merge added no new id. `merged` is a
  // superset of `existing`'s unique ids by construction, so equal sets ⟺ the
  // merge introduced nothing — compare sizes rather than positions so a
  // reordered (but same-membership) stored array still returns the original.
  const existingUnique = new Set(existing.filter(Boolean));
  if (merged.length === existingUnique.size) {
    return metadata;
  }

  return { ...metadata, message_ids: merged };
}
